import {
  loadTemplateSettings,
  saveTemplateSettings,
  updateUserBrowsableWebsitePublishedAt,
} from "../../../Utils/SupabaseServices/TemplateDatabase";
import { getCreatorPublicBaseUrl } from "../../../Utils/url/getCreatorPublicBaseUrl";
import { isBrowsablePlatformDomain } from "../../../Utils/url/buildCreatorPublicUrl";
import { supabase } from "../../../config/supabaseClient";
import {
  FALLBACK_TEMPLATE_TWEAKS,
  mapTemplateSettingsRow,
} from "../MasterTemplateEditor/templateSettingsMap";
import type { GettingStartedRecord, Tweaks } from "../MasterTemplateEditor/types";
import type { SiteBuilderPersisted } from "./siteBuilderTypes";
import { isCreatorPagesMode } from "../../../Utils/componentsTable";

export type GettingStartedProgress = GettingStartedRecord;

const PREFIX = "gs_home_progress_v1:";
const TOUR_COMPLETED_KEY = "website_tour_completed_v1";
const TOUR_ARM_KEY = "website_onboarding_start_tour_v1";

function storageKey(username: string) {
  return `${PREFIX}${username}`;
}

export function emptyGettingStartedProgress(): GettingStartedProgress {
  return {
    tourDone: false,
    exploreDone: false,
    websiteTourDone: false,
    publishedAt: null,
    editedAt: null,
    linkInBioEditedAt: null,
    siteBuiltAt: null,
    onboardingCallBooked: false,
    socialsDone: false,
    announceDone: false,
    builder: null,
  };
}

function asIso(value: unknown): string | null {
  const raw = String(value || "").trim();
  return raw ? raw : null;
}

export function normalizeGettingStartedProgress(
  raw?: Partial<GettingStartedProgress> | null,
): GettingStartedProgress {
  const defaults = emptyGettingStartedProgress();
  if (!raw || typeof raw !== "object") return defaults;
  return {
    tourDone: Boolean(raw.tourDone),
    exploreDone: Boolean(raw.exploreDone),
    websiteTourDone: Boolean(raw.websiteTourDone),
    publishedAt: asIso(raw.publishedAt),
    editedAt: asIso(raw.editedAt),
    linkInBioEditedAt: asIso(raw.linkInBioEditedAt),
    siteBuiltAt: asIso(raw.siteBuiltAt),
    onboardingCallBooked: Boolean(raw.onboardingCallBooked),
    socialsDone: Boolean(raw.socialsDone),
    announceDone: Boolean(raw.announceDone),
    builder:
      raw.builder && typeof raw.builder === "object" ? raw.builder : null,
  };
}

export function hasEditedWebsite(progress: GettingStartedProgress) {
  // Published website only — draft edits that were discarded do not count.
  return Boolean(progress.editedAt || progress.publishedAt);
}

/** Starter/Pro: Add & edit your content — true after a real LiB add/edit. */
export function hasEditedLinkInBio(progress: GettingStartedProgress) {
  return Boolean(progress.linkInBioEditedAt);
}

/** @deprecated Prefer `hasEditedWebsite` — this name conflated publish with edit. */
export function hasPublishedWebsite(progress: GettingStartedProgress) {
  return hasEditedWebsite(progress);
}

export function isWebsitePublished(progress: GettingStartedProgress) {
  return Boolean(progress.publishedAt);
}

export function getGettingStartedPublicLink(
  username?: string | null,
  userData?: {
    url_type?: string | null;
    domain?: string | null;
    custom_domain?: string | null;
  } | null,
  customDomain?: string | null,
): { href: string; display: string } {
  const handle = String(username || "").trim();
  if (!handle) return { href: "", display: "" };
  const savedDomain = String(userData?.domain || "").trim();
  const resolvedCustom =
    String(customDomain || "").trim() ||
    String(userData?.custom_domain || "").trim() ||
    (savedDomain && !isBrowsablePlatformDomain(savedDomain) ? savedDomain : "");
  const href = getCreatorPublicBaseUrl({
    username: handle,
    customDomain: resolvedCustom || undefined,
    browsableUrlPrefs: userData
      ? {
          urlType: (userData.url_type as "path" | "subdomain" | null) ?? null,
          domain: userData.domain ?? null,
        }
      : undefined,
  });
  return { href, display: href.replace(/^https?:\/\//, "") };
}

export function loadGettingStartedProgress(
  username?: string | null,
): GettingStartedProgress {
  const defaults = emptyGettingStartedProgress();
  if (!username) return defaults;
  try {
    const raw = localStorage.getItem(storageKey(username));
    if (!raw) {
      return {
        ...defaults,
        websiteTourDone: isWebsiteEditorTourCompleted(username),
      };
    }
    const parsed = JSON.parse(raw);
    return {
      ...normalizeGettingStartedProgress(parsed),
      websiteTourDone:
        Boolean(parsed?.websiteTourDone) || isWebsiteEditorTourCompleted(username),
    };
  } catch {
    return defaults;
  }
}

export function saveGettingStartedProgress(
  username: string | null | undefined,
  progress: GettingStartedProgress,
) {
  if (!username) return;
  try {
    localStorage.setItem(storageKey(username), JSON.stringify(progress));
    if (progress.websiteTourDone) {
      localStorage.setItem(tourKey(TOUR_COMPLETED_KEY, username), "true");
    }
  } catch {
    // ignore quota / private mode
  }
}

function tourKey(base: string, username?: string | null) {
  const handle = String(username || "").trim().toLowerCase();
  return handle ? `${base}:${handle}` : base;
}

export function isWebsiteEditorTourCompleted(username?: string | null) {
  try {
    return localStorage.getItem(tourKey(TOUR_COMPLETED_KEY, username)) === "true";
  } catch {
    return false;
  }
}

export function isWebsiteEditorTourArmed(username?: string | null) {
  try {
    return localStorage.getItem(tourKey(TOUR_ARM_KEY, username)) === "true";
  } catch {
    return false;
  }
}

export function armWebsiteEditorTour(username?: string | null) {
  try {
    localStorage.setItem(tourKey(TOUR_ARM_KEY, username), "true");
  } catch {
    // ignore
  }
}

export function disarmWebsiteEditorTour(username?: string | null) {
  try {
    localStorage.removeItem(tourKey(TOUR_ARM_KEY, username));
    if (username) localStorage.removeItem(TOUR_ARM_KEY);
  } catch {
    // ignore
  }
}

/** Persist skip/complete so leftover router state cannot reopen the tour. */
export function markWebsiteEditorTourCompleted(username?: string | null) {
  try {
    localStorage.setItem(tourKey(TOUR_COMPLETED_KEY, username), "true");
    localStorage.removeItem(tourKey(TOUR_ARM_KEY, username));
    if (username) localStorage.removeItem(TOUR_ARM_KEY);
  } catch {
    // ignore
  }
}

/** First-time website setup for this user should not inherit another account's skip. */
export function clearWebsiteEditorTourCompleted(username?: string | null) {
  try {
    localStorage.removeItem(tourKey(TOUR_COMPLETED_KEY, username));
    localStorage.removeItem(TOUR_COMPLETED_KEY);
  } catch {
    // ignore
  }
}

export async function loadGettingStartedProgressFromDb(
  userId?: string | null,
): Promise<GettingStartedProgress | null> {
  if (!userId?.trim()) return null;
  const row = await loadTemplateSettings(userId);
  if (!row) return null;
  const { tweaks } = mapTemplateSettingsRow(row);
  return normalizeGettingStartedProgress(tweaks.gettingStarted);
}

export async function patchGettingStartedProgressOnDb(
  userId: string | null | undefined,
  username: string | null | undefined,
  partial: Partial<GettingStartedProgress>,
): Promise<GettingStartedProgress | null> {
  if (!userId?.trim()) return null;

  const row = await loadTemplateSettings(userId);
  const mapped = row
    ? mapTemplateSettingsRow(row)
    : { tweaks: FALLBACK_TEMPLATE_TWEAKS, creatorData: {} };

  const next = {
    ...normalizeGettingStartedProgress(mapped.tweaks.gettingStarted),
    ...partial,
  };
  const tweaks: Tweaks = { ...mapped.tweaks, gettingStarted: next };

  const payload = {
    tweaks: tweaks as unknown as Record<string, unknown>,
  };
  const result = row
    ? await saveTemplateSettings(userId, payload)
    : await saveTemplateSettings(userId, {
        ...payload,
        bio: {},
        highlights: [],
        links: [],
        banners: {},
        carousels: [],
        products: [],
        press: [],
        about: {},
        recent: [],
      });

  if (!result.ok) return null;

  if (next.publishedAt) {
    void updateUserBrowsableWebsitePublishedAt(userId, next.publishedAt);
  }

  saveGettingStartedProgress(username, next);
  return next;
}

/**
 * Starter/Pro Home checklist: mark "Add & edit your content" after a real
 * Link in Bio add/edit (not on visit). Idempotent.
 *
 * Always writes `linkInBioEditedAt` for classic LiB surfaces — Home only
 * surfaces this for Starter/Pro; Growth ignores the field. Do not gate on
 * plan here (misdetected Growth would silently skip the checklist mark).
 *
 * Classic LiB surfaces only — skip embed / website-template / creator pages.
 */
export function shouldMarkLinkInBioEditFromSurface(from?: string | null) {
  const surface = String(from || "").trim();
  if (surface === "embed-search" || surface === "website-template") {
    return false;
  }
  if (isCreatorPagesMode(surface)) return false;
  return true;
}

export const GETTING_STARTED_PROGRESS_EVENT = "gs-home-progress";

function notifyGettingStartedProgress(username: string) {
  try {
    window.dispatchEvent(
      new CustomEvent(GETTING_STARTED_PROGRESS_EVENT, {
        detail: { username },
      }),
    );
  } catch {
    // ignore (SSR / non-DOM)
  }
}

export async function markLinkInBioContentEdited(
  userId?: string | null,
  username?: string | null,
  from?: string | null,
): Promise<void> {
  if (!shouldMarkLinkInBioEditFromSurface(from)) return;

  let uid = String(userId || "").trim();
  let handle = String(username || "").trim();
  if (!uid && !handle) return;

  try {
    if (!uid || !handle) {
      let query = supabase
        .from("user")
        .select("user_id, username");
      query = uid ? query.eq("user_id", uid) : query.eq("username", handle);
      const { data } = await query.maybeSingle();
      if (!data) return;
      uid = String(data.user_id || uid).trim();
      handle = String(data.username || handle).trim();
    }

    if (!uid || !handle) return;

    const local = loadGettingStartedProgress(handle);
    if (local.linkInBioEditedAt) {
      notifyGettingStartedProgress(handle);
      return;
    }

    const at = new Date().toISOString();
    saveGettingStartedProgress(handle, {
      ...local,
      linkInBioEditedAt: at,
    });
    await patchGettingStartedProgressOnDb(uid, handle, {
      linkInBioEditedAt: at,
    });
    notifyGettingStartedProgress(handle);
  } catch {
    // non-blocking — checklist mark must never break LiB edits
  }
}

export function builderSnapshotForDb(
  state: SiteBuilderPersisted,
): Record<string, unknown> {
  const photo = String(state.profileEdits?.photo || "");
  const profileEdits = state.profileEdits
    ? {
        name: state.profileEdits.name,
        bio: state.profileEdits.bio,
        photo: photo.startsWith("data:") ? "" : photo,
      }
    : null;
  return {
    version: 1,
    started: state.started,
    building: state.building,
    siteBuilt: state.siteBuilt,
    stepIdx: state.stepIdx,
    pages: state.pages,
    linkinbio: state.linkinbio,
    themeId: state.themeId,
    fontPair: state.fontPair || "Default",
    buildStarted: state.buildStarted,
    builtCount: state.builtCount,
    profileEdits,
  };
}

export const ONBOARDING_CALL_URL =
  "https://calendly.com/d/crfd-hbg-hrc/search-bubble-onboarding-call";

export const ONBOARDING_CALENDLY_EMBED_URL =
  "https://calendly.com/browsable/intro-call-clone?text_color=000000&primary_color=8282f4";
